import React, { useState } from 'react';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { useWindDirectionFrequency, useMonthlyWeatherStatistics } from '../../api/weatherHistory';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import { toDisplaySpeed } from '../../utils/units';

interface WindRoseViewProps {
  projectId: string;
}

const WindRoseView: React.FC<WindRoseViewProps> = ({ projectId }) => {
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [hoveredMonth, setHoveredMonth] = useState<number | null>(null);
  const unitSystem = useUnitSystem();
  const { data: windDirections, isLoading: isLoadingWind } = useWindDirectionFrequency(projectId, selectedMonth);
  const { data: monthlyStats, isLoading: isLoadingMonthly } = useMonthlyWeatherStatistics(projectId);

  const formatFrequencyPercent = (value: unknown) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    const pct = n <= 1 ? n * 100 : n;
    return `${pct.toFixed(1)}%`;
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const windSpeedData = monthlyStats.map((stat) => ({
    month: monthNames[stat.month - 1],
    speed: toDisplaySpeed(stat.avg_wind_speed, unitSystem),
  }));

  if (isLoadingMonthly) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#ff6b35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Wind Direction Frequency - Radar Chart */}
        <Grid item xs={6}>
          <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 3 }}>
              Wind Direction Frequency
            </Typography>
            <Box sx={{ position: 'relative' }}>
              {isLoadingWind && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0)',
                    zIndex: 1,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress sx={{ color: '#ff6b35' }} />
                </Box>
              )}
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart data={windDirections}>
                <PolarGrid stroke="rgba(255, 255, 255, 0.2)" />
                <PolarAngleAxis
                  dataKey="direction"
                  tick={{ fill: 'rgba(255, 255, 255, 0.7)', fontSize: 14 }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 'auto']}
                  tick={{ fill: 'rgba(255, 255, 255, 0.5)' }}
                  tickFormatter={formatFrequencyPercent}
                />
                <Radar
                  name="Frequency"
                  dataKey="frequency"
                  stroke="#9c27b0"
                  fill="#9c27b0"
                  fillOpacity={0.6}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value: any, name: string) => {
                    if (name === 'frequency' || name === 'Frequency') {
                      return [formatFrequencyPercent(value), 'Frequency'];
                    }
                    return [value, name];
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', display: 'block', mt: 2 }}
            >
              Historical wind distribution ({selectedMonth ? monthNames[selectedMonth - 1] : 'All Year'})
            </Typography>
          </Box>
        </Grid>

        {/* Wind Speed Averages - Bar Chart */}
        <Grid item xs={6}>
          <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 3 }}>
              Wind Speed Averages
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={windSpeedData}
                onClick={(data) => {
                  const payloadMonth = data?.activePayload?.[0]?.payload?.month;
                  if (!payloadMonth) return;

                  const monthIndex = monthlyStats.findIndex(
                    stat => monthNames[stat.month - 1] === payloadMonth
                  );
                  const clickedMonth = monthlyStats[monthIndex]?.month;
                  setSelectedMonth(selectedMonth === clickedMonth ? null : clickedMonth);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                <XAxis
                  dataKey="month"
                  stroke="rgba(255, 255, 255, 0.5)"
                  tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
                />
                <YAxis
                  stroke="rgba(255, 255, 255, 0.5)"
                  tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
                  label={{
                    value: unitSystem === 'imperial' ? 'mph' : 'km/h',
                    angle: -90,
                    position: 'insideLeft',
                    fill: 'rgba(255, 255, 255, 0.7)',
                  }}
                />
                <Bar dataKey="speed" radius={[8, 8, 0, 0]} cursor="pointer">
                  {windSpeedData.map((entry, index) => {
                    const monthIndex = monthlyStats.findIndex(
                      stat => monthNames[stat.month - 1] === entry.month
                    );
                    const monthNumber = monthlyStats[monthIndex]?.month;
                    const isSelected = selectedMonth === monthNumber;
                    const isHovered = hoveredMonth === monthNumber;
                    return (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={isSelected ? '#ff6b35' : isHovered ? '#00e5ff' : '#03a9f4'}
                        stroke={isHovered && !isSelected ? 'rgba(255, 255, 255, 0.9)' : undefined}
                        strokeWidth={isHovered && !isSelected ? 2 : 0}
                        onMouseEnter={() => {
                          if (monthNumber) setHoveredMonth(monthNumber);
                        }}
                        onMouseLeave={() => {
                          setHoveredMonth(null);
                        }}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WindRoseView;
